import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import {
	Tile,
	H4,
	Spacer,
	Grid,
	Select,
	Datepicker,
	Timepicker,
	Radio,
	Label,
	TextInput,
	Button,
	Dialog,
	toast,
	WarnChip,
	ErrorChip,
	InfoChip
} from '@components';
import styles from '../EditShipment.module.scss';
import {
	timeSlotStatuses,
	TIMESLOT_STATUS_ENUM,
	TShiperETAReason,
	BUTTON_SIZE,
	PartialInputEvent,
	TCargoEOS,
	TShipperETAResponse,
	SHIPMENT_STATUS
} from '@types';
import { useValidation } from '@hooks';
import z, { RefinementCtx } from 'zod';
import { ADMIN_ETA_REDIRECT_REASON, formatDate, isDateWithin60Days } from '@utils'
import { usePostETARequest, useValidateETARequest } from '@api';
import { EditShipment, IsAdmin } from '@state';
import { EditShipmentContext } from '../EditShipment.Context';

type TProps = {
	destination: 'pickup' | 'dropoff';
	reasons: TShiperETAReason[];
	statusCode?: number;
	cargo: TCargoEOS;
	updateETA: (eta: TShipperETAResponse) => void;
}

const defaultForm = {
	reason: '',
	arrivalDate: '',
	arrivalFrom: '',
	departBy: '',
	timeSlot: '',
	slotDetails: ''
} as {
	reason: string;
	arrivalDate: string | Date;
	arrivalFrom: string;
	departBy: string;
	timeSlot: string;
	slotDetails: string;
}

export const Request = (props: TProps) => {
	const {
		destination,
		reasons,
		statusCode,
		cargo,
		updateETA
	} = props;
	const ctx = useContext(EditShipmentContext);
	const originalDate = destination === 'pickup' ? cargo.original_pickup_at : cargo.original_drop_at;
	const editWindowExpired = !isDateWithin60Days(originalDate);
	const requestBookingDisabled = (ctx?.access.pages.updateETA.fields.requestBooking.disabled ?? false) || editWindowExpired;
	const requestBookingReason = ctx?.access.pages.updateETA.reason;
	const showTimeSlot = (statusCode ?? 0) < SHIPMENT_STATUS.DELIVERED;
	const shipment = useRecoilValue(EditShipment);
	const isAdmin = useRecoilValue(IsAdmin);
	const [form, setForm] = useState(defaultForm);
	const [showConfirm, setShowConfirm] = useState(false);
	const [formReset, setFormReset] = useState(false);
	const [rateCardWarning, setRateCardWarning] = useState<string | null>(null);
	const [rateCardError, setRateCardError] = useState<string | null>(null);
	const { connect, formValid, clearErrors } = useValidation({
		reason: z.string({ required_error: 'A reason is required' }).min(1, { message: 'A reason is required' }),
		arrivalDate: z.date({ required_error: 'Arrival date is required' }).or(z.string().min(1, { message: 'Arrival date is required' })),
		arrivalFrom: z.string({ required_error: 'Arrive required' }).min(1, { message: 'Arrive required' }),
		departBy: z.string({ required_error: 'Depart required' }).min(1, { message: 'Depart required' }),
		timeSlot: z.string()
	}, {
		superRefine: (data: any, ctx: RefinementCtx) => {
			if (data.arrivalDate && !isDateWithin60Days(data.arrivalDate)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Date must be within 60 days in the past or future',
					path: ['arrivalDate']
				});
			} else if (editWindowExpired) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Cannot edit after 60 days',
					path: ['arrivalDate']
				});
			}
		}
	});

	const {
		isLoadingPostETA,
		callPostETA
	} = usePostETARequest();

	const { isLoadingValidateETA, callValidateETA } = useValidateETARequest();

	const getMinDate = () => {
		if (destination === 'pickup') return undefined;
		return new Date(cargo.pickup_at);
	}

	const getMaxDate = () => {
		if (destination === 'dropoff') return undefined;
		return new Date(cargo.drop_at);
	}

	const updateForm = (name: string, value: string | Date) => {
		setForm({
			...form,
			[name]: value
		});
		setRateCardWarning(null);
		setRateCardError(null);
	}

	const disableSubmit = () => {
		if (
			form.reason !== '' &&
			form.arrivalDate !== '' &&
			form.arrivalFrom !== '' &&
			form.departBy !== '' &&
			(!showTimeSlot || form.timeSlot !== '')
		) {
			if (showTimeSlot && form.timeSlot === TIMESLOT_STATUS_ENUM.BOOKED && form.slotDetails === '') {
				return true;
			}
			return false;
		}
		return true;
	}

	const getRequestTimeSlotRequirements = (selectedTimeSlotOption: string, timeSlotName: string) => {
		const requirements = {
			carrier_must_book_slot: false,
			carrier_must_call_before: false,
			has_time_slot: false,
			is_mandatory: false,
			slot: '',
		};
		switch (selectedTimeSlotOption) {
		case TIMESLOT_STATUS_ENUM.OFLOAD_MUST_BOOK:
			requirements.carrier_must_book_slot = true;
			break;
		case TIMESLOT_STATUS_ENUM.BOOKED:
			requirements.carrier_must_call_before = true;
			requirements.has_time_slot = true;
			requirements.is_mandatory = true;
			requirements.slot = timeSlotName;
			break;
		default:
			break;
		}
		return requirements;
	};

	const realSaveETA = async () => {
		const data = {
			cargo_id: cargo.id,
			user_type: 'shipper',
			arrival_type: destination === 'pickup' ? 'Pickup' : 'DropOff',
			request_arrival_date: formatDate(new Date(form.arrivalDate || new Date()), 'API_DATE_FORMAT'),
			request_arrival_start_time: form.arrivalFrom,
			request_arrival_end_time: form.departBy,
			reason: form.reason,
			request_time_slot_requirements: showTimeSlot ? getRequestTimeSlotRequirements(form.timeSlot, form.slotDetails) : undefined,
		}
		await callPostETA({ payload: data }).then((res) => {
			if (res) {
				toast.success('Your booking time request has been submitted successfully');
				setForm(defaultForm);
				setRateCardWarning(null);
				updateETA(res.data.data);
				setFormReset(true);
				ctx?.setETASubmission && ctx.setETASubmission(true);
			}
		}).catch((err) => {
			if (err && err.cause) {
				toast.error('Something went wrong trying to add a booking time');
				console.error(err);
			}
		});
	}

	const buildPayload = () => ({
		cargo_id: cargo.id,
		user_type: 'shipper',
		arrival_type: destination === 'pickup' ? 'Pickup' : 'DropOff',
		request_arrival_date: formatDate(new Date(form.arrivalDate || new Date()), 'API_DATE_FORMAT'),
		request_arrival_start_time: form.arrivalFrom,
		request_arrival_end_time: form.departBy,
		reason: form.reason,
		request_time_slot_requirements: showTimeSlot ? getRequestTimeSlotRequirements(form.timeSlot, form.slotDetails) : undefined,
	});

	const proceedToStore = () => {
		if (isAdmin || !statusCode || statusCode <= 10) {
			realSaveETA();
		} else {
			setShowConfirm(true);
		}
	}

	const preSave = () => {
		const isValid = formValid(form);
		if (!isValid.success) return;

		// Admin already acknowledged the warning — proceed to store
		if (isAdmin && rateCardWarning !== null) {
			proceedToStore();
			return;
		}

		callValidateETA({ payload: buildPayload() })
			.then((res) => {
				const warning = res?.data?.data?.rate_card_warning ?? null;
				if (warning) {
					setRateCardWarning(warning);
				} else {
					proceedToStore();
				}
			})
			.catch((err) => {
				const errData = err?.cause?.response?.data;
				const reason = errData?.errors?.reason?.[0] ?? 'An error occurred validating the booking time';
				setRateCardError(reason);
			});
	}

	useEffect(() => {
		if (formReset) {
			setTimeout(() => {
				clearErrors();
			}, 0); // --> Wait to the end of the JS Stack to execute this...
			setFormReset(false);
		}
	}, [formReset]);

	return (
		<>
			<Tile stretch hasBorder>
				<H4>Request a new booking time</H4>
				<Spacer size={12} />
				{rateCardWarning && (
					<>
						<WarnChip text={rateCardWarning} />
						<Spacer size={8} />
					</>
				)}
				{rateCardError && (
					<>
						<ErrorChip text={rateCardError} />
						<Spacer size={8} />
					</>
				)}
				{form.arrivalDate && (form.arrivalDate instanceof Date || String(form.arrivalDate).length >= 10) && (() => { const d = new Date(form.arrivalDate); return !isNaN(d.getTime()) && [0, 6].includes(d.getDay()); })() && (
					<>
						<InfoChip text={'You have selected a weekend. Extra charges may apply.'} />
						<Spacer size={8} />
					</>
				)}
				{requestBookingReason === ADMIN_ETA_REDIRECT_REASON && <>
					<WarnChip text={
						<>Please perform this action via the <Link to={`/transport/all-loads?search=${encodeURIComponent(shipment?.reference ?? '')}`} style={{ color: 'inherit', textDecoration: 'underline' }}>Transport page</Link></>
					} />
					<Spacer size={12} />
				</>}
				<Grid columns={4} minWidth={'100px'} columnTemplate='repeat(4, minmax(100px, 1fr))'>
					<Grid.Item columnSpan={4}>
						<Select
							label={'Reason'}
							name={'reason'}
							value={form.reason}
							onChange={(e) => updateForm('reason', e)}
							required
							disabled={requestBookingDisabled}
							{...connect('reason')}
						>
							{reasons.map((reason, i) => {
								return <Select.Option key={i} value={reason.key}>{reason.name}</Select.Option>
							})}
						</Select>
					</Grid.Item>
					<Grid.Item columnSpan={2}>
						<Datepicker
							label={'Arrival Date'}
							name={'arrivalDate'}
							value={form.arrivalDate}
							onChange={(e) => updateForm('arrivalDate', (e as PartialInputEvent).target.value)}
							required
							keepErrorSpace={false}
							minDate={getMinDate()}
							maxDate={getMaxDate()}
							disabled={requestBookingDisabled}
							{...connect('arrivalDate')}
						/>
					</Grid.Item>
					<Timepicker
						label={'Arrival from'}
						name={'arrivalFrom'}
						value={form.arrivalFrom}
						onChange={(e) => updateForm('arrivalFrom', e as string)}
						required
						returnNotSynthetic
						keepErrorSpace={false}
						disabled={requestBookingDisabled}
						{...connect('arrivalFrom')}
					/>
					<Timepicker
						label={'Depart by'}
						name={'departBy'}
						value={form.departBy}
						onChange={(e) => updateForm('departBy', e as string)}
						required
						returnNotSynthetic
						keepErrorSpace={false}
						disabled={requestBookingDisabled}
						{...connect('departBy')}
					/>
					{showTimeSlot && <>
						<Grid.Item columnSpan={2}>
							<Label required>Time Slot details</Label>
							<div className={styles.radioGroup}>
								<Radio.Group className={styles.spaced} {...connect('timeSlot')}>
									{timeSlotStatuses.map((ts, i) => {
										return <Radio
											key={i}
											name={'timeSlot'}
											value={ts.value}
											onChange={() => !requestBookingDisabled && updateForm('timeSlot', ts.value)}
											label={ts.labelShort}
											checked={form.timeSlot === ts.value}
											disabled={requestBookingDisabled}
										/>
									})}
								</Radio.Group>
							</div>
						</Grid.Item>
						<Grid.Item columnSpan={2} alignSelf={'end'}>
							{form.timeSlot === TIMESLOT_STATUS_ENUM.BOOKED && <TextInput
								placeholder={'Tell us about the time slot'}
								name={'slotDetails'}
								value={form.slotDetails}
								onChange={(e) => updateForm('slotDetails', e.target.value)}
								required
								keepErrorSpace={false}
								disabled={requestBookingDisabled}
							/>}
						</Grid.Item>
					</>}
				</Grid>
				<Spacer size={16} />
				<div className={styles.btns}>
					<Button
						size={BUTTON_SIZE.SMALL}
						text={'Request'}
						onClick={preSave}
						loading={isLoadingPostETA || isLoadingValidateETA}
						disabled={disableSubmit() || requestBookingDisabled || (!isAdmin && !!rateCardError) || (!isAdmin && !!rateCardWarning)}
					/>
				</div>
			</Tile>
			<Dialog
				open={showConfirm}
				onClose={() => setShowConfirm(false)}
				title={'Update requested booking time'}
				text={`Are you sure you would like to update the requested time, as this shipment has already been allocated, updating the requested time may incur a charge.`}
				actionButtonFn={realSaveETA}
				actionButtonText={'Confirm'}
				actionButtonLoading={isLoadingPostETA}
				actionButtonActsAsCancel
			/>
		</>
	);
}
